import type { ConfigType } from 'dayjs'
import dayjs from 'dayjs'
import type { Duration } from 'dayjs/plugin/duration'

import type { NonEmptyArray } from '../types/arrays'
import type { TimeRange } from './types'

export function max<T extends ConfigType>(...times: NonEmptyArray<T>): T {
  return times.reduce((max, time) => (dayjs(time).isAfter(max) ? time : max))
}

export function min<T extends ConfigType>(...times: NonEmptyArray<T>): T {
  return times.reduce((min, time) => (dayjs(time).isBefore(min) ? time : min))
}

export function copyTimeOfDay(src: ConfigType, dest: ConfigType) {
  const srcDayjs = dayjs(src)
  return dayjs(dest)
    .set('hour', srcDayjs.hour())
    .set('minute', srcDayjs.minute())
    .set('second', srcDayjs.second())
    .set('millisecond', srcDayjs.millisecond())
}

/**
 * Returns the duration between two times, represented as a dayjs `Duration` object
 */
export function getDuration(start: ConfigType, end: ConfigType): Duration {
  return dayjs.duration(dayjs(end ?? undefined).diff(start))
}

/**
 * Picks the utilization aggregation interval for a requested range: windows of 2 days or longer use
 * the coarser 15-minute aggregate, shorter windows keep 1-minute resolution. Coarser buckets on wide
 * ranges keep both the chart and the server-side aggregation cheap.
 *
 * The range is first normalized to whole-minute bounds (start-of-minute / end-of-minute) so the
 * decision matches the window actually queried via `useValidatedDateRange`, not the raw sub-minute
 * selection — otherwise a range just under 2 days could round up past the threshold while still
 * requesting 1-minute resolution.
 */
export function utilizationIntervalForRange(timeRange: TimeRange): '1m' | '15m' {
  const from = dayjs(timeRange.from).startOf('minute')
  const to = dayjs(timeRange.to).endOf('minute')
  return getDuration(from, to).asDays() >= 2 ? '15m' : '1m'
}

/**
 * Returns a valid IANA timezone string, falling back to UTC for invalid values.
 * Sites may have "UNKNOWN" as their timezone if not configured, which is not a valid IANA timezone.
 */
export function getSiteTimezone(timezone: string | undefined | null): string {
  if (!timezone || timezone === 'UNKNOWN') {
    return 'UTC'
  }
  return timezone
}
